import * as React from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import { Controller, useFormContext } from "react-hook-form";
import type { FieldPath, FieldValues } from "react-hook-form";

import { useTranslation } from "react-i18next";

export interface CheckboxFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>
> {
  name: TName;
  label: React.ReactNode;
}

const AtlanteCheckboxBase = styled("span")(() => ({
  width: 24,
  height: 24,
  borderRadius: 6,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
}));

const AtlanteCheckboxUncheckedRoot = styled(AtlanteCheckboxBase)(() => ({
  border: "2px solid #C5CDD8",
  backgroundColor: "#F5F7FA",
}));

const AtlanteCheckboxCheckedRoot = styled(AtlanteCheckboxBase)(() => ({
  backgroundImage: "linear-gradient(135deg, #6CCB8B 0%, #3975D0 100%)",
  color: "#FFFFFF",
}));

function AtlanteCheckboxUncheckedIcon() {
  return <AtlanteCheckboxUncheckedRoot />;
}

function AtlanteCheckboxCheckedIcon() {
  return (
    <AtlanteCheckboxCheckedRoot>
      <CheckIcon sx={{ fontSize: 14 }} />
    </AtlanteCheckboxCheckedRoot>
  );
}

export function CheckboxField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ name, label }: CheckboxFieldProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>();
  const { t } = useTranslation();

  const translateError = (m?: string) => (m ? t(m, { defaultValue: m }) : "");

  return (
    <FormControl component="fieldset" variant="standard">
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  icon={<AtlanteCheckboxUncheckedIcon />}
                  checkedIcon={<AtlanteCheckboxCheckedIcon />}
                  color="default"
                  sx={{
                    mr: 1,
                    p: 0,
                  }}
                />
              }
              label={label}
              sx={{
                alignItems: "flex-start",
                "& .MuiTypography-root": {
                  color: "#4A4A4A",
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                },
              }}
            />

            {fieldState.error && (
              <FormHelperText error sx={{ mt: 0.25 }}>
                {translateError(fieldState.error.message)}
              </FormHelperText>
            )}
          </>
        )}
      />
    </FormControl>
  );
}
